import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { MdKeyboardArrowRight, MdKeyboardArrowDown } from 'react-icons/md';
import styles from './style/EnterpriseList.module.scss';
import TextField from '../Utilities/TextField';
import AlertSwitch from '../Utilities/AlertSwitch';
import Datepicker from '../Utilities/Datepicker/Datepicker';
import Tips from '../Utilities/Tips';
import { getServiceName } from '../../constant/SERVICE_NAME_MAP';
import {
  getStatusMeta,
  toPlanForm,
  validatePlanCard,
  isNoSeatServiceItem,
  toPlanAllocations,
  buildPlans,
  buildNoSeatPlans,
  validateSeatCard,
  noSeatUsageKey,
} from './planCardForm';
import { isUsageBlocked, isServiceSwitchDisabled } from './usageGate';

// 起訖日安全格式化：無值或非法值回 null（不丟例外）。
const safeDate = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'yyyy-MM-dd');
};

/**
 * 單一服務的訂閱式開通卡片（批次版，plans 統一）。
 *
 * 不再區分訂閱制／席位制：一律依 menuItem.avaliable_plans 有幾筆 render 幾列席位輸入。
 * 多列時每列前面加 rental_fee 標籤以區分；單列則不加。
 * 卡片不單獨存檔：透過 reportCard 回報「目前 payload（plans）/ 合法性 / 變更 / 啟用中席位數」給彈窗，
 * 由彈窗「全部儲存」一次批次送出（只送有變更的服務）。
 * 無席位服務（sys_alias 為 data-center／automation）隱藏席位輸入，送出時帶 unlimited_seats: true，
 * 後端把 users_count_upper_limit 存 null＝不設席位上限。
 */
const PlanCard = ({ data, auth, permissions, menu, reportCard, usageEnabled = true }) => {
  const status = getStatusMeta(data);
  const menuItem = (menu || []).find((m) => m.id === data?.software_services_id);
  const plans = menuItem?.avaliable_plans || [];
  // 無席位設定的服務：隱藏席位欄位，送出時標記為不設席位上限
  // 以 menu 的 sys_alias 判斷（跨環境穩定），menu 未載入時才退回 id 保底
  const noSeatService = isNoSeatServiceItem(menuItem, data?.software_services_id);
  // usage-api 的 service key（'data_center' / 'automation'）；非年度方案為 null。
  // 先在此算成字串（primitive），供 effect 依賴，避免把 menuItem 物件塞進 deps。
  const usageKey = noSeatService ? noSeatUsageKey(menuItem, data?.software_services_id) : null;

  const [form, setForm] = useState(() => toPlanForm(data));
  const [alloc, setAlloc] = useState(() => toPlanAllocations(data, plans));
  const [errors, setErrors] = useState({});
  // 黏著旗標：使用者動過一次就算有變更，直到還原／重載才清掉。
  const [isDirty, setIsDirty] = useState(false);
  // 卡片展開／收合：點卡片標題列可切換。預設依「是否已啟用」決定（啟用者展開）。
  const [expanded, setExpanded] = useState(() => !!data?.activation_state);

  useEffect(() => {
    const next = toPlanForm(data);
    setForm(next);
    setAlloc(toPlanAllocations(data, menuItem?.avaliable_plans || []));
    setErrors({});
    setIsDirty(false);
    setExpanded(!!next.activate);
  }, [data, menuItem?.avaliable_plans]);

  const serviceName = getServiceName(
    menuItem?.name ||
      data?.software_services_name ||
      data?.service_name ||
      data?.name ||
      menuItem?.name_local
  );
  const hasPlan = plans.length > 0;
  const multiPlan = plans.length > 1;
  const canManage =
    auth?.roles?.find((it) => it.includes('workspace.admin')) ||
    permissions?.includes('tdp.services.enterprise.activate_services_enterprise_bought.center') ||
    permissions?.includes('tdp.services.enterprise.stop_services_enterprise_bought.center');
  const readOnly = !canManage;
  // 未啟用「用量管理」時不可開通服務：鎖住開關並提示。已開通中的服務不受此限。
  // usageEnabled 由彈窗以「當前（未儲存）用量開關」算出，不是伺服器已儲存值。
  const usageBlocked = isUsageBlocked({ usageEnabled, activate: form.activate });
  const switchDisabled = isServiceSwitchDisabled({
    readOnly,
    hasPlan,
    usageEnabled,
    activate: form.activate,
  });
  // 各方案分配人數總和
  const allocTotal = plans.reduce((sum, p) => sum + (Number(alloc?.[p.id]) || 0), 0);

  // 每次表單/分配變動，回報最新 payload / 合法性 / 變更旗標 / 啟用中席位數給彈窗。
  useEffect(() => {
    if (!reportCard || data?.software_services_id == null) return;
    const list = menuItem?.avaliable_plans || [];
    const seatSum = list.reduce((sum, p) => sum + (Number(alloc?.[p.id]) || 0), 0);
    // 無席位服務：不佔席位（users_count = 0），僅帶一筆 plan_id 供後端決定主方案
    const planItems = noSeatService ? buildNoSeatPlans(list) : buildPlans(list, alloc);
    // 用量配額換算：無席位服務（資料中心 / 任務自動化）為年度方案、按企業計價，
    // **不計入**用量管理的建議席位總數（使用者裁示 2026-08-27）。故 effectiveSeat = 0，
    // 不再「以 1 席計入」。它們的容量改由各自的「容量上限（GB）」→ usage-api 管理。
    const effectiveSeat = noSeatService ? 0 : seatSum;

    const payload = {
      service_id: data.software_services_id,
      start_date: safeDate(form.start_date),
      expiration_date: safeDate(form.expiration_date),
      activate: form.activate,
      is_trial: form.is_trial,
      plans: planItems,
    };
    // 不設席位上限：後端據此把 users_count_upper_limit 存 null
    if (noSeatService) payload.unlimited_seats = true;
    // 無席位服務只驗日期（席位跳過）；其餘用分配版驗證
    let validation = noSeatService
      ? validatePlanCard(form, { skipSeat: true })
      : validateSeatCard(form, alloc);
    // 無席位服務另驗容量上限（GB）：需為 0 或正數，且不得留空。
    if (noSeatService) {
      const q = form.quota_gb;
      const qNum = Number(q);
      if (q === '' || q == null || !Number.isFinite(qNum) || qNum < 0) {
        validation = {
          ok: false,
          errors: { ...validation.errors, quota_gb: '容量上限需為 0 或正數' },
        };
      }
    }

    reportCard(data.software_services_id, {
      serviceName,
      payload,
      valid: validation.ok,
      errors: validation.errors,
      dirty: isDirty,
      activeSeat: form.activate ? effectiveSeat : 0,
      // 年度方案服務的容量上限（GB）寫 usage-api enterprise_service_quotas；
      // usageKey = usage-api 的 service key（'data_center' / 'automation'）。非年度方案為 null。
      usageKey,
      quotaGb: noSeatService ? Number(form.quota_gb) : null,
    });
  }, [
    reportCard,
    data?.software_services_id,
    serviceName,
    form,
    alloc,
    isDirty,
    noSeatService,
    usageKey,
    menuItem?.avaliable_plans,
  ]);

  // 卡片卸載時從彈窗登記表移除
  useEffect(
    () => () => reportCard?.(data?.software_services_id, null),
    [reportCard, data?.software_services_id]
  );

  const patch = (partial) => {
    setForm((prev) => ({ ...prev, ...partial }));
    setErrors({});
    setIsDirty(true);
  };
  const setAllocValue = (planId, val) => {
    setAlloc((prev) => ({ ...prev, [planId]: val === '' ? '' : Number(val) }));
    setErrors({});
    setIsDirty(true);
  };

  return (
    <div className={styles.mpCard}>
      {/* 點標題列可展開／收合本卡片；開關等互動元件各自 stopPropagation 不觸發收合。 */}
      <div
        className={styles.mpCardTop}
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((e) => !e)}
      >
        <div className={styles.planCardLeft}>
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            {usageBlocked && hasPlan && !readOnly ? (
              <Tips
                detail='請先啟用「用量管理」，才能開通服務'
                side='top'
                align='start'
                topDiff={6}
              >
                <AlertSwitch
                  disabled
                  isChecked={form.activate}
                  onChange={() => {}}
                  color={{ on: '#5856D6', off: '#5856D64D' }}
                />
              </Tips>
            ) : (
              <AlertSwitch
                disabled={switchDisabled}
                isChecked={form.activate}
                onChange={() => {
                  if (!form.activate) setExpanded(true);
                  patch({ activate: !form.activate });
                }}
                color={{ on: '#5856D6', off: '#5856D64D' }}
              />
            )}
          </span>
          <span className={styles.planCardName}>{serviceName}</span>
          <span className={`${styles.statusBadge} ${styles[status.cls]}`}>{status.label}</span>
          {!hasPlan && <span className={styles.tagUnpurchased}>尚未設定方案</span>}
        </div>
        <div className={styles.rowActions}>
          <span className={styles.planCardChevron}>
            {expanded ? <MdKeyboardArrowDown size={18}/> : <MdKeyboardArrowRight size={18}/>}
          </span>
        </div>
      </div>

      {expanded && (
        <div
          className={styles.trialPanel}
          style={{
            margin: '16px 0 0',
            padding: 0,
            border: 'none',
            background: 'transparent',
            borderRadius: 0,
          }}
        >
          <>
            <div className={styles.trialPanelField}>
              <label>使用期間</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div className={styles.trialPanelDates}>
                  <Datepicker
                    selectDate={form.start_date}
                    setSelectDate={(e) => {
                      if (readOnly) return;
                      patch({ start_date: e });
                    }}
                    disabled={readOnly}
                    error={!!errors.start_date}
                  />
                  <span>至</span>
                  <Datepicker
                    selectDate={form.expiration_date}
                    setSelectDate={(e) => {
                      if (readOnly) return;
                      patch({ expiration_date: e });
                    }}
                    disabled={readOnly}
                    error={!!errors.expiration_date}
                  />
                </div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type='checkbox'
                    checked={form.is_trial}
                    disabled={readOnly}
                    onChange={() => patch({ is_trial: !form.is_trial })}
                    style={{ accentColor: '#5856D6' }}
                  />
                  試用
                </span>
              </div>
            </div>

            {/* 無席位服務（資料中心 / 任務自動化）：以「容量上限（GB）」取代席位輸入。
                值寫入 usage-api 的 enterprise_service_quotas，作為該服務超量 enforcement 的單一真相。 */}
            {noSeatService && (
              <div className={styles.trialPanelField}>
                <label>容量上限</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 240 }}>
                  <TextField
                    placeholder='請輸入'
                    width='100%'
                    numeric
                    value={form.quota_gb}
                    onChange={(val) => patch({ quota_gb: val === '' ? '' : Number(val) })}
                    error={!!errors.quota_gb}
                    min={0}
                    isDisabled={readOnly}
                  />
                  <span>GB</span>
                </div>
              </div>
            )}

            {/* 無席位服務不顯示席位輸入；其餘依 available_plans 有幾筆 render 幾列 */}
            {!noSeatService && hasPlan && (
              <div className={styles.trialPanelField}>
                <label>席位上限</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        ...(multiPlan
                          ? {
                              padding: '8px 12px',
                              border: '1px solid #e5e5ea',
                              borderRadius: 8,
                              background: '#fff',
                            }
                          : {}),
                      }}
                    >
                      {/* 多列時以 rental_fee 前置標籤區分各方案 */}
                      {multiPlan && (
                        <span style={{ fontWeight: 600, minWidth: 88 }}>NT${plan.rental_fee}</span>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                        <TextField
                          placeholder='請輸入'
                          width='100%'
                          numeric
                          value={alloc[plan.id] ?? 0}
                          onChange={(val) => setAllocValue(plan.id, val)}
                          error={!!errors.allocations || !!errors.seat}
                          min={0}
                          isDisabled={readOnly}
                        />
                        <span>人</span>
                      </div>
                    </div>
                  ))}
                  {multiPlan && (
                    <label>
                      <span>總分配人數</span>
                      <span> {allocTotal} 人</span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </>
        </div>
      )}
    </div>
  );
};

export default PlanCard;
